/**
 * SocialModal — overlay rendering the platform's native iframe at full size.
 *
 * On the grid we render our own SocialCard chrome (visual consistency).
 * When a user clicks the card we drop the platform's own iframe inside
 * a centered modal — so playback, ad attribution, comments, etc. all
 * happen on the platform's surface without ever sending the user off-site.
 *
 * Close: Escape key or clicking the backdrop.
 */

import React, { useCallback, useEffect } from "react";
import { FaTimes } from "react-icons/fa";

export type SocialModalSource = "INSTAGRAM" | "TIKTOK" | "X";

export interface SocialModalPayload {
    source: SocialModalSource;
    /** Platform's own post identifier (IG shortcode, TikTok video id, X tweet id) */
    postId: string;
    /** Author handle — needed for X to construct the embed iframe */
    authorHandle?: string;
}

interface Props {
    payload: SocialModalPayload | null;
    onClose: () => void;
}

function buildEmbedSrc(payload: SocialModalPayload): string {
    const id = encodeURIComponent(payload.postId);
    switch (payload.source) {
        case "INSTAGRAM":
            return `https://www.instagram.com/p/${id}/embed/captioned/`;
        case "TIKTOK":
            return `https://www.tiktok.com/embed/v2/${id}`;
        case "X": {
            /*
             * Twitter's platform.twitter.com syndication renders the full tweet card
             * (author, photo, replies link, like/retweet counts) inside an iframe with
             * the official embed styling. Theme=dark matches our hub chrome.
             * `origin` param scopes the iframe's postMessage handshake to our domain.
             */
            const origin = typeof window !== "undefined" ? window.location.origin : "https://devourplay.gg";
            return `https://platform.twitter.com/embed/Tweet.html?id=${id}&theme=dark&hideCard=false&hideThread=false&dnt=true&origin=${encodeURIComponent(origin)}`;
        }
        default:
            return "";
    }
}

export const SocialModal: React.FC<Props> = ({ payload, onClose }) => {
    const handleKey = useCallback((e: KeyboardEvent) => {
        if (e.key === "Escape") onClose();
    }, [onClose]);

    useEffect(() => {
        if (!payload) return;
        document.addEventListener("keydown", handleKey);
        const prevOverflow = document.body.style.overflow;
        document.body.style.overflow = "hidden";
        return () => {
            document.removeEventListener("keydown", handleKey);
            document.body.style.overflow = prevOverflow;
        };
    }, [payload, handleKey]);

    if (!payload) return null;

    const embedSrc = buildEmbedSrc(payload);

    return (
        <div className="hcs-social-modal" role="dialog" aria-modal="true" onClick={onClose}>
            <button type="button" className="hcs-social-modal__close" aria-label="Close" onClick={onClose}>
                <FaTimes />
            </button>
            <div className={`hcs-social-modal__frame hcs-social-modal__frame--${payload.source.toLowerCase()}`}
                onClick={e => e.stopPropagation()}>
                <iframe
                    src={embedSrc}
                    title="Social post"
                    frameBorder={0}
                    /*
                     * `scrolling="auto"` matters for X — the tweet embed sizes itself
                     * via postMessage, and media/quote/thread cards routinely overflow
                     * the 90vh frame. Auto-scroll prevents silent content clipping.
                     */
                    scrolling="auto"
                    allow="encrypted-media; picture-in-picture; autoplay; fullscreen"
                    allowTransparency
                    allowFullScreen
                />
            </div>
        </div>
    );
};
